import { fetchAddAddress, fetchUpdateAddress } from '@/api/account';
import { Address } from '@/api/types/public';
import { handleDiffCountry } from '@/app/(payment)/payment/util';
import AddressForm from '@/components/Address';
import { useStore } from '@/providers/app-store-provider';
import { EPaymentMethod } from '@/utils/enums/order';
import { env } from '@/utils/env';
import { getAddressRules, getErrorTranslationKey } from '@/utils/form-rule';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AddressActionType } from '../../Address';

export interface AddressComponentPcProps {
  withoutAddress: boolean;
  shippingAddress: Address;
  addressList: Address[];
  selectedAddress: Address;
  SDMyAddressPageBtnClick: (btn_name: string) => void;
  countryIsSame: boolean;
  updateSelectedAddress: (address: Address) => void;
  paymentType: string;
  ref?: React.RefObject<any>;
  SDAddressEditPageBtnClick: (btn_name: string, is_save_success: boolean) => void;
  SDAddressEditPageView: () => void;
  handleUpdateAddressList: (id?: number) => void;
}

export default function AddressComponentPc({
  shippingAddress,
  addressList: _addressList,
  selectedAddress,
  countryIsSame,
  SDMyAddressPageBtnClick,
  updateSelectedAddress,
  paymentType,
  withoutAddress,
  ref,
  SDAddressEditPageBtnClick,
  SDAddressEditPageView,
  handleUpdateAddressList,
}: AddressComponentPcProps) {
  // 把actionType 暴露出去
  useImperativeHandle(ref, () => ({
    actionType,
  }));

  const [actionType, setActionType] = useState(AddressActionType.DEFAULT);
  const countryList = useStore((state) => state.countryList);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'address-popper' : undefined;
  const tValidation = useTranslations('components.address.validation');
  const t = useTranslations('payment.BillingAddress');

  const defaultAddress = {
    firstname: '',
    lastname: '',
    country_id: env.NEXT_PUBLIC_ADDRESS_COUNTRY_ID,
    country: env.NEXT_PUBLIC_ADDRESS_COUNTRY,
    street: '',
    apartment: '',
    city: '',
    province: '',
    province_id: '',
    region_code: '',
    postcode: '',
    tel_code: env.NEXT_PUBLIC_ADDRESS_TEL_CODE,
    tel_country: env.NEXT_PUBLIC_ADDRESS_TEL_COUNTRY,
    telephone: '',
    cpf: '', // 巴西税号
    default: 2,
  };

  const [addressFormData, setAddressFormData] = useState<Address>(defaultAddress);

  useEffect(() => {
    if (actionType !== AddressActionType.DEFAULT) {
      SDAddressEditPageView();
    }
  }, [actionType]);

  const addNew = () => {
    setAddressFormData(defaultAddress);
    setActionType(AddressActionType.ADD);
    setAnchorEl(null);
  };

  // 编辑地址
  const editAddress = (address: Address) => {
    const toEditAddress = [EPaymentMethod.AFTERPAY, EPaymentMethod.KLARNA].includes(paymentType)
      ? handleDiffCountry(address, shippingAddress, countryList)
      : address;
    setActionType(AddressActionType.EDIT);
    setAddressFormData(toEditAddress);
    setAnchorEl(null);
  };

  const handleCancel = () => {
    SDAddressEditPageBtnClick('Cancel', false);
    setActionType(AddressActionType.DEFAULT);
  };

  // 切换地址
  const chooseAddress = (item: Address) => {
    if (item.status === 2 || !item.checked) return;
    if (disabledAddressItem(item)) return;

    updateSelectedAddress(item);
    setAnchorEl(null);
  };

  const countryInvalid = (address: Address) => {
    let flag = false;
    if (address.status === 2) {
      flag = true;
    }
    return flag;
  };

  const disabledAddressItem = (address: Address) => {
    const rule1 = [EPaymentMethod.AFTERPAY, EPaymentMethod.KLARNA].includes(paymentType);
    const rule2 = address.country_id !== shippingAddress.country_id;
    return rule1 && rule2;
  };

  // 处理点击事件，打开/关闭地址列表
  const handleClick = () => {
    if (_addressList.length === 0) return;
    setAnchorEl(anchorEl ? null : containerRef.current);
  };

  const [loading, setLoading] = useState(false);

  const handleOnSubmit = async (address: Address) => {
    setLoading(true);

    // let updatedAddress = address;
    let handleId = 0;

    if (actionType === 'ADD') {
      // 新增
      // 这里的toast隐藏掉，因为它的弹出和全局的loading重复了
      const { code, data } = await fetchAddAddress(address, false);
      if (code !== 200) {
        SDAddressEditPageBtnClick('Save', false);
        setLoading(false);
        return;
      }
      handleId = data?.id || 0;
      // 新增成功后，使用返回的完整地址信息（包含ID）
      // updatedAddress = data || address;
    } else {
      // 修改
      // 这里的toast隐藏掉，因为它的弹出和全局的loading重复了
      const { code } = await fetchUpdateAddress(address.id, address, false);
      if (code !== 200) {
        SDAddressEditPageBtnClick('Save', false);
        setLoading(false);
        return;
      }
      handleId = address.id;
    }

    SDAddressEditPageBtnClick('Save', true);
    setLoading(false);

    // updateSelectedAddress(updatedAddress);
    handleUpdateAddressList(handleId);

    setActionType(AddressActionType.DEFAULT);
  };

  const getErrorMessage = (_address: Address) => {
    const errorCode = getAddressRules(_address, countryList);
    return errorCode && getErrorTranslationKey(errorCode)
      ? tValidation(getErrorTranslationKey(errorCode)!)
      : '';
  };

  useEffect(() => {
    if (!selectedAddress) {
      setActionType(AddressActionType.DEFAULT);
    }
  }, [selectedAddress]);

  // 这里暂时不考虑 countryIsSame
  useEffect(() => {
    if (withoutAddress) {
      setActionType(AddressActionType.ADD);
    } else {
      setActionType(AddressActionType.DEFAULT);
    }
  }, [withoutAddress]);

  return (
    <div>
      <div className="flex justify-between items-center mt-20 mb-16">
        <h3 className="font-bb ">{t('BillingAddress')}</h3>
        {actionType === AddressActionType.DEFAULT && (
          <span
            className="add text-primary-purple font-br underline cursor-pointer"
            onClick={() => {
              addNew();
              SDMyAddressPageBtnClick('Add New Address');
            }}
          >
            + {t('add')}
          </span>
        )}
      </div>
      <ClickAwayListener
        onClickAway={() => {
          setAnchorEl(null);
        }}
      >
        <div>
          {actionType === AddressActionType.DEFAULT && (
            <div
              ref={containerRef}
              onClick={handleClick}
              className={`py-[13px] px-[max(1.25vw,16px)]  cursor-pointer box-border bg-white border border-[#CCCCCC] rounded-[0.52vw] flex justify-between items-center ${!countryIsSame ? 'border-[#bc1b1b]' : ''}`}
            >
              {!selectedAddress?.id ? (
                <span className="text-[max(0.9375vw,14px)] text-secondary-text font-br">
                  {t('select')}
                </span>
              ) : (
                <div className="w-full text-primary-black">
                  <p className="text-16 font-br leading-[1.2] break-words">
                    {selectedAddress.address}
                  </p>
                  <p className="mt-[5px]">
                    <span className="text-[14px] mr-[1.04vw] break-words">
                      {selectedAddress.firstname} {selectedAddress.lastname}
                    </span>
                    <span className="text-[14px] break-words">{selectedAddress.telephone}</span>
                    {selectedAddress.default === 1 && (
                      <span className="inline-block py-[5px] px-[8px] ml-[0.52vw] text-14 bg-primary-purple text-white rounded-[12px]">
                        {t('default')}
                      </span>
                    )}
                  </p>
                  <p
                    className="mt-[12px] inline-block text-primary-purple text-[14px] font-bb underline cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      editAddress(selectedAddress);
                      SDMyAddressPageBtnClick('Edit');
                    }}
                  >
                    {t('edit')}
                  </p>
                  {!countryIsSame && (
                    <div className="invalid-tips text-[#BC1B1B] mt-2">
                      {t('billingCountryMustMatchShippingCountry')}
                    </div>
                  )}
                  {!!getErrorMessage(selectedAddress) && (
                    <div className="invalid-tips text-[#BC1B1B] mt-2">
                      {getErrorMessage(selectedAddress)}
                    </div>
                  )}
                </div>
              )}

              {_addressList.length > 0 && (
                <CollapseArrowDownOutlined
                  className={`text-[22px] text-secondary-text transition-all duration-300 ${
                    anchorEl ? 'rotate-[180deg]' : ''
                  }`}
                />
              )}
            </div>
          )}
          {actionType !== AddressActionType.DEFAULT && (
            <AddressForm
              data={addressFormData}
              close={handleCancel}
              bizType="billing"
              onSubmit={handleOnSubmit}
              type={actionType as 'ADD' | 'EDIT'}
              disabledCountry={[EPaymentMethod.AFTERPAY, EPaymentMethod.KLARNA].includes(
                paymentType,
              )}
              loading={loading}
              isNeedPagePadding={false}
              stateAndZipLayout={'vertical'}
            />
          )}

          <Popper
            id={id}
            open={open}
            anchorEl={anchorEl}
            sx={{
              width: containerRef.current?.clientWidth,
              border: '1px solid #CCCCCC',
              borderRadius: '10px',
              paddingLeft: '30px',
              paddingRight: '30px',
              boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.2)',
              backgroundColor: 'white',
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          >
            {_addressList.length > 0 &&
              _addressList.map((item) => {
                const errorMsg = getErrorMessage(item);
                return (
                  <div
                    key={item.id}
                    onClick={() => chooseAddress(item)}
                    className={`border-b border-solid border-[#CCCCCC] py-12 cursor-pointer ${
                      !item.checked || countryInvalid(item) || disabledAddressItem(item)
                        ? '!cursor-not-allowed text-auxiliary-text'
                        : ''
                    }`}
                  >
                    <p
                      className={`text-[14px] leading-1.2 text-primary-black ${
                        !item.checked || countryInvalid(item) || disabledAddressItem(item)
                          ? '!text-auxiliary-text'
                          : ''
                      }`}
                    >
                      {item.address}
                    </p>
                    <p className="mt-10">
                      <span className="text-sm mr-20 break-words">
                        {item.firstname} {item.lastname}
                      </span>
                      <span className="text-sm break-words">{item.telephone}</span>
                      {item.default === 1 && (
                        <span className="inline-block py-[5px] px-[8px]  text-[14px] bg-primary-purple text-white rounded-[12px] ml-12">
                          {t('default')}
                        </span>
                      )}
                    </p>
                    <p
                      className="mt-3 text-primary-purple text-[14px] font-bb underline cursor-pointer inline-block"
                      onClick={(e) => {
                        e.stopPropagation();
                        editAddress(item);
                      }}
                    >
                      {t('edit')}
                    </p>
                    {!!errorMsg && (
                      <div className="text-[#BC1B1B] mt-12 text-[14px]">{errorMsg}</div>
                    )}
                  </div>
                );
              })}
          </Popper>
        </div>
      </ClickAwayListener>
    </div>
  );
}
